"use client";

import { Check, ChevronDown, LoaderCircle, MapPin, Sparkles, X } from "lucide-react";
import { useLinkStatus } from "next/link";
import Link from "@/components/ui/Link";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import { hasFilters, type StreamQuery, streamsHref } from "@/lib/stream-filters";
import { STREAM_GENDERS, STREAM_REGIONS } from "@/lib/stream-platforms";
import { useUi } from "@/components/ui/use-ui";

/** The choices' counts (lib/streams.ts getStreamFacets). */
export interface Facets {
  genders: Partial<Record<string, number>>;
  regions: Partial<Record<string, number>>;
  tags: { tag: string; count: number }[];
  newCount: number;
}

export function Pending() {
  const { pending } = useLinkStatus();
  const t = useUi();
  return pending ? <LoaderCircle size={13} className="animate-spin" aria-label={t.common.loading} /> : null;
}

/** The choices worth offering: only what the platform's data has, plus whatever is chosen. */
export function filterChoices(query: StreamQuery, facets: Facets) {
  return {
    genders: STREAM_GENDERS.filter((g) => (facets.genders[g.id] ?? 0) > 0 || g.id === query.gender),
    allGenders: Object.values(facets.genders).reduce<number>((sum, n) => sum + (n ?? 0), 0),
    regions: STREAM_REGIONS.filter((r) => (facets.regions[r.id] ?? 0) > 0 || r.id === query.region),
    hasNew: facets.newCount > 0 || !!query.isNew,
    tags: query.tag && !facets.tags.some((t) => t.tag === query.tag) ? [{ tag: query.tag, count: 0 }, ...facets.tags] : facets.tags,
  };
}

export function Chip({ href, active, count, children, className }: { href: string; active: boolean; count?: number; children: React.ReactNode; className?: string }) {
  return (
    <Link
      href={href}
      scroll={false}
      aria-current={active ? "true" : undefined}
      className={cn(
        "flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13.5px] transition-colors",
        active ? "bg-white text-black" : "chip text-soft hover:text-fg",
        className,
      )}
    >
      {children}
      {count !== undefined && <span className={cn("tabular-nums text-[12px]", active ? "text-black/55" : "text-muted")}>{compact(count)}</span>}
      <Pending />
    </Link>
  );
}

/** "Region" as a chip that opens the list of regions. */
function RegionMenu({ query, facets, regions }: { query: StreamQuery; facets: Facets; regions: (typeof STREAM_REGIONS)[number][] }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const t = useUi();
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const current = STREAM_REGIONS.find((r) => r.id === query.region);
  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13.5px] transition-colors",
          current ? "bg-white text-black" : "chip text-soft hover:text-fg",
        )}
      >
        <MapPin size={14} />
        {current ? t.cams.regions[current.id] : t.cams.region}
        <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div role="menu" className="glass-strong absolute left-0 top-full z-30 mt-2 w-60 rounded-xl p-1.5 shadow-[0_20px_60px_-15px_rgb(0_0_0/0.9)]">
          {[{ id: undefined, label: t.cams.allRegions }, ...regions.map((r) => ({ id: r.id, label: t.cams.regions[r.id] }))].map((r) => {
            const on = r.id === query.region;
            return (
              <Link
                key={r.id ?? "all"}
                role="menuitem"
                href={streamsHref({ ...query, region: r.id })}
                scroll={false}
                onClick={() => setOpen(false)}
                className={cn("flex h-9 items-center gap-2 rounded-lg px-3 text-[13.5px]", on ? "bg-white/10 text-fg" : "text-soft hover:bg-white/[0.06] hover:text-fg")}
              >
                <span className="flex-1">{r.label}</span>
                {r.id && <span className="tabular-nums text-[12px] text-muted">{compact(facets.regions[r.id] ?? 0)}</span>}
                {on && <Check size={14} />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * What is chosen, above the streams on wider screens (the choices themselves are in the left menu):
 * one chip per platform or filter, each removing itself.
 */
export function ActiveFilters({ query }: { query: StreamQuery }) {
  const t = useUi();
  const chips = [
    query.platform && { label: query.platform.name, href: streamsHref({ platform: null, gender: query.gender, region: query.region, isNew: query.isNew }) },
    query.gender && { label: t.cams.genders[query.gender], href: streamsHref({ ...query, gender: undefined }) },
    query.region && { label: t.cams.regions[query.region], href: streamsHref({ ...query, region: undefined }) },
    query.isNew && { label: t.cams.newModels, href: streamsHref({ ...query, isNew: undefined }) },
    query.tag && { label: `#${query.tag}`, href: streamsHref({ ...query, tag: undefined }) },
  ].filter((c): c is { label: string; href: string } => !!c && !!c.label);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <Link key={c.label} href={c.href} scroll={false} className="flex h-8 items-center gap-1.5 rounded-full bg-white px-3 text-[13px] text-black hover:bg-white/85">
          {c.label}
          <X size={13} aria-label={t.cams.remove} />
          <Pending />
        </Link>
      ))}
      {chips.length > 1 && (
        <Link href="/" scroll={false} className="px-2 text-[13px] text-muted hover:text-fg">
          {t.cams.clearAll}
        </Link>
      )}
    </div>
  );
}

/**
 * Narrow the live streams: who is on camera, region, new performers, and the most used tags, each with
 * how many streams it would show. Only the filters the platform's data supports appear. These are the
 * rows above the streams on phones, which have no menu rail; wider screens have them in the left menu
 * (StreamMenu).
 */
export function StreamFilters({ query, facets }: { query: StreamQuery; facets: Facets }) {
  const { genders, allGenders, regions, hasNew, tags } = filterChoices(query, facets);
  const t = useUi();
  const removeTag = t.cams.removeTag;
  if (!genders.length && !regions.length && !hasNew && !tags.length) return null;

  return (
    <div className="space-y-2.5">
      <div className="no-scrollbar -mx-3 flex items-center gap-2 overflow-x-auto px-3 pb-0.5 md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
        {genders.length > 0 && (
          <>
            <Chip href={streamsHref({ ...query, gender: undefined })} active={!query.gender} count={allGenders}>
              {t.cams.all}
            </Chip>
            {genders.map((g) => (
              <Chip key={g.id} href={streamsHref({ ...query, gender: g.id })} active={query.gender === g.id} count={facets.genders[g.id] ?? 0}>
                {t.cams.genders[g.id]}
              </Chip>
            ))}
            <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-white/10" />
          </>
        )}
        {regions.length > 0 && <RegionMenu query={query} facets={facets} regions={regions} />}
        {hasNew && (
          <Chip href={streamsHref({ ...query, isNew: query.isNew ? undefined : true })} active={!!query.isNew} count={facets.newCount}>
            <Sparkles size={14} />
            {t.cams.newModels}
          </Chip>
        )}
        {hasFilters(query) && (
          <Link
            href={streamsHref({ platform: query.platform })}
            scroll={false}
            className="flex h-9 shrink-0 items-center gap-1 rounded-full px-3 text-[13.5px] text-muted hover:text-fg"
          >
            <X size={14} />
            {t.cams.clear}
          </Link>
        )}
      </div>

      {tags.length > 0 && (
        <nav aria-label={t.cams.tags} className="no-scrollbar -mx-3 flex gap-2 overflow-x-auto px-3 pb-1 md:mx-0 md:px-0">
          {tags.map((t) => {
            const on = t.tag === query.tag;
            return (
              <Chip key={t.tag} href={streamsHref({ ...query, tag: on ? undefined : t.tag })} active={on} count={on ? undefined : t.count} className="h-8 px-3 text-[13px]">
                #{t.tag}
                {on && <X size={13} aria-label={removeTag} />}
              </Chip>
            );
          })}
        </nav>
      )}
    </div>
  );
}
